import React, { useState } from "react";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import DataTable from "../../components/Elements/DataTable/DataTable";
import MainButton from "../../components/Elements/MainButton/MainButton";

import AdminCreatePromocodeModal from "../../components/AdminPages/AdminCreatePromocodeModal";
import AdminDeletePromocodeModal from "../../components/AdminPages/AdminDeletePromocodeModal";
import AdminEditPromocodeModal from "../../components/AdminPages/AdminEditPromocodeModal";

import { fetchPromocodesAdmin } from "../../services/VoucherService";

import editIcon from "../../assets/data/edit.png";
import deleteIcon from "../../assets/data/delete.png";

// Always visible promocode columns
const alwaysVisibleColumns = [
  { key: "code", label: "Code", width: "200px" },
  { key: "type", label: "Type", width: "150px" },
  { key: "amount", label: "Amount", width: "300px" },
  { key: "activations_left", label: "Activations Left", width: "300px" },
  { key: "user_activation_count", label: "Activation Count", width: "300px" },
  { key: "date_end", label: "Expires", width: "300px" },
];

// No extra optional columns yet (kept for future extension)
const optionalColumns = [{ key: "currency", label: "Currency", width: "150px" }];

// Sorting/filters currently disabled, placeholders for future
const sortOptions = [
  { value: "code", label: "Code" },
  { value: "type", label: "Type" },
  { value: "amount", label: "Amount" },
  { value: "currency", label: "Currency" },
  { value: "activations_left", label: "Activations Left" },
  { value: "user_activation_count", label: "Activation Count" },
  { value: "date_end", label: "Expires" },
];

const filterDefinitions = [
  {
    key: "code",
    type: "text",
  },
  {
    key: "type",
    type: "select",
    options: [
      { value: "topup_amount", label: "Topup Amount" },
      { value: "sell_discount", label: "Sell Discount" },
    ],
  },
  {
    key: "currency",
    type: "text",
  },
  {
    key: "amount",
    type: "minmax",
    inputType: "number",
    minKey: "amount_min",
    maxKey: "amount_max",
  },
  {
    key: "activations_left",
    type: "minmax",
    inputType: "number",
    minKey: "activations_left_min",
    maxKey: "activations_left_max",
  },
  {
    key: "user_activation_count",
    type: "minmax",
    inputType: "number",
    minKey: "user_activation_count_min",
    maxKey: "user_activation_count_max",
  },
  {
    key: "date_end",
    type: "date-range",
    minKey: "date_end_min",
    maxKey: "date_end_max",
  },
];

// Custom renderers for amount and date
const promocodeRenderers = {
  amount: (item) => {
    if (item.type === "sell_discount") {
      if (item.amount == null) return "-";
      return `${item.amount}%`;
    }
    if (item.amount == null) return "-";
    return `${item.amount} ${item.currency || ""}`.trim();
  },
  date_end: (item) => {
    if (!item.date_end) return "-";
    return new Date(item.date_end).toLocaleDateString("pl-PL");
  },
};

const AdminPromocodes = () => {
  const [dataVersion, setDataVersion] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [editingPromocode, setEditingPromocode] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedPromocode, setSelectedPromocode] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Data loader for DataTable
  const fetchDataFn = (params) => fetchPromocodesAdmin(params);

  // Modal callbacks: create/edit/delete
  const handleCreated = () => {
    setShowCreateModal(false);
    setDataVersion((v) => v + 1);
  };

  const handleEdit = (promo) => {
    setEditingPromocode(promo);
    setShowEditModal(true);
  };

  const handleUpdated = () => {
    setShowEditModal(false);
    setEditingPromocode(null);
    setDataVersion((v) => v + 1);
  };

  const handleDelete = (promo) => {
    setSelectedPromocode(promo);
    setShowDeleteModal(true);
  };

  const handleDeleted = () => {
    setShowDeleteModal(false);
    setSelectedPromocode(null);
    setDataVersion((v) => v + 1);
  };

  const createButton = <MainButton onClick={() => setShowCreateModal(true)}>Create +</MainButton>;

  const promocodeRowActions = [
    { label: "Edit", icon: editIcon, onClick: handleEdit },
    { label: "Delete", icon: deleteIcon, onClick: handleDelete },
  ];

  return (
    <div className="admin-promocodes-page">
      <PageHeader title="Promocodes" description="View, edit and manage all promocodes" actions={createButton} />

      <DataTable
        key={dataVersion}
        tableId="admin-promocodes"
        fetchDataFunction={fetchDataFn}
        columns={alwaysVisibleColumns}
        optionalColumns={optionalColumns}
        sortOptions={sortOptions}
        filterDefinitions={filterDefinitions}
        rowActions={promocodeRowActions}
        customRenderers={promocodeRenderers}
        defaultSortBy="code"
        itemKey="code"
        showCheckboxes={false}
        showFilters={true}
        showPageSize={true}
        showSort={true}
      />

      <AdminCreatePromocodeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreated}
      />

      <AdminEditPromocodeModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        promocode={editingPromocode}
        onUpdated={handleUpdated}
      />

      <AdminDeletePromocodeModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        promocode={selectedPromocode}
        onDeleted={handleDeleted}
      />
    </div>
  );
};

export default AdminPromocodes;
